import { createFileRoute, Link } from "@tanstack/react-router";
import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/hooks/useAuth";
import {
  useSubscriptions,
  useSalary,
  useInflation,
  useProfile,
  computeSubscriptionTotals,
  computeSalaryGrowth,
  computeNominalRealSeries,
  cumulativeCpiFactor,
} from "@/lib/queries";
import {
  formatNok,
  formatMoney,
  formatPercent,
  formatMonth,
  monthlyEquivalent,
} from "@/lib/format";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";
import { useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { InfoTooltip } from "@/components/InfoTooltip";
import {
  FREE_VISIBLE_LIMIT,
  rankSalaryForGate,
  rankSubscriptionsForGate,
  topNIds,
  useIsFree,
} from "@/lib/free-limits";
import {
  BlurredGateWrapper,
  GateStatsNote,
  UpgradeGateRow,
  UpgradeLockPanel,
} from "@/components/UpgradeGate";
import { NominalRealChart } from "@/components/AnalyticsCharts";

export const Route = createFileRoute("/_authenticated/dashboard")({
  head: () => ({ meta: [{ title: "Overview — VISIKT" }] }),
  component: Dashboard,
});

function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const subs = useSubscriptions();
  const sal = useSalary();
  const infl = useInflation();

  const isFree = useIsFree();
  const allSubs = useMemo(() => subs.data ?? [], [subs.data]);
  const allSal = useMemo(() => sal.data ?? [], [sal.data]);
  const subGateIds = useMemo(
    () => (isFree ? topNIds(rankSubscriptionsForGate(allSubs), FREE_VISIBLE_LIMIT) : null),
    [isFree, allSubs],
  );
  const salGateIds = useMemo(
    () => (isFree ? topNIds(rankSalaryForGate(allSal), FREE_VISIBLE_LIMIT) : null),
    [isFree, allSal],
  );
  const subsForStats = useMemo(
    () => (subGateIds ? allSubs.filter((s) => subGateIds.has(s.id)) : allSubs),
    [allSubs, subGateIds],
  );
  const salForStats = useMemo(
    () => (salGateIds ? allSal.filter((s) => salGateIds.has(s.id)) : allSal),
    [allSal, salGateIds],
  );
  const hiddenSubs = isFree ? Math.max(0, allSubs.length - FREE_VISIBLE_LIMIT) : 0;
  const totals = useMemo(() => computeSubscriptionTotals(subsForStats), [subsForStats]);
  const currentYear = new Date().getFullYear();
  const growthRowsAll = useMemo(
    () => computeSalaryGrowth(salForStats, infl.data ?? []),
    [salForStats, infl.data],
  );
  const growthRows = useMemo(
    () => (isFree ? growthRowsAll.filter((r) => r.year === currentYear) : growthRowsAll),
    [isFree, growthRowsAll, currentYear],
  );
  const nominalRealSeries = useMemo(
    () => computeNominalRealSeries(growthRows, infl.data ?? []),
    [growthRows, infl.data],
  );
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const selected =
    growthRows.find((r) => r.year === selectedYear) ?? growthRows[growthRows.length - 1];
  const profile = useProfile();
  const displayName =
    profile.data?.display_name?.trim() ||
    [profile.data?.first_name, profile.data?.last_name].filter(Boolean).join(" ").trim() ||
    (user?.user_metadata?.display_name as string | undefined) ||
    user?.email?.split("@")[0] ||
    "there";

  // Latest gross monthly salary (from latest salary entry) — drives the ring.
  const latestGrossMonthly = useMemo(() => {
    const sorted = [...salForStats].sort((a, b) =>
      b.effective_date.localeCompare(a.effective_date),
    );
    const latest = sorted[0];
    return latest ? Number(latest.gross_annual_nok) / 12 : null;
  }, [salForStats]);

  const subsShareOfSalary =
    latestGrossMonthly != null && latestGrossMonthly > 0
      ? Math.round((totals.monthly / latestGrossMonthly) * 1000) / 10
      : null;

  // Paused (inactive) subs savings.
  const pausedSubs = subsForStats.filter((s) => !s.is_active);
  const pausedSavingsMonthly = pausedSubs.reduce(
    (acc, s) => acc + monthlyEquivalent(Number(s.monthly_cost_nok_equiv), s.billing_cycle),
    0,
  );

  // Cumulative purchasing power line
  const cumulative = useMemo(() => {
    if (growthRows.length < 2) return null;
    const first = growthRows[0];
    const last = growthRows[growthRows.length - 1];
    const salaryGrowth = ((last.salary - first.salary) / first.salary) * 100;
    const cpiFactor = cumulativeCpiFactor(infl.data ?? [], first.year, last.year);
    const cpiGrowth = (cpiFactor - 1) * 100;
    const realDiff = ((1 + salaryGrowth / 100) / cpiFactor - 1) * 100;
    return { fromYear: first.year, salaryGrowth, cpiGrowth, realDiff };
  }, [growthRows, infl.data]);

  // For the dashboard preview list, show up to 4 in canonical order but blur
  // any that fall outside the Free top-3 set (identical to /subscriptions).
  const upcoming = allSubs.filter((s) => s.is_active).slice(0, 4);
  const baseYear = growthRows[0]?.year;

  return (
    <AppShell>
      {/* Hero */}
      <section className="animate-entry">
        <header className="mb-6">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
            {t("dashboard.eyebrow")} / {formatMonth(new Date())}
          </p>
          <h1 className="text-2xl font-display font-bold tracking-tight">
            {t("dashboard.hello", { name: displayName })}
          </h1>
          <p className="mt-3 text-[11px] text-muted-foreground leading-relaxed max-w-prose">
            {t("dashboard.adviceNotice")}
          </p>
        </header>

        {subsShareOfSalary != null ? (
          <div className="flex items-end gap-6">
            <div className="relative size-32 rounded-full border-[10px] border-foreground/5 grid place-items-center overflow-hidden">
              <div
                className="absolute inset-0"
                style={{
                  background: `conic-gradient(var(--color-accent) 0% ${Math.min(100, subsShareOfSalary)}%, transparent ${Math.min(100, subsShareOfSalary)}% 100%)`,
                }}
              />
              <div className="absolute inset-[8px] bg-background rounded-full" />
              <div className="relative text-center">
                <span className="block font-mono text-[9px] text-muted-foreground leading-none mb-1 inline-flex items-center gap-1">
                  {t("dashboard.fixedRing")}
                  <InfoTooltip
                    text={t("dashboard.fixedRingHelp")}
                    label={t("dashboard.fixedRing")}
                  />
                </span>
                <span className="block font-display text-lg font-bold">{subsShareOfSalary}%</span>
              </div>
            </div>
            <div className="flex-1 space-y-1 min-w-0">
              <div className="font-mono text-[11px] text-muted-foreground">
                {t("dashboard.fixedMonthly")}
              </div>
              <div className="text-3xl font-display font-bold tabular-nums truncate">
                {totals.monthly > 0 ? formatNok(totals.monthly) : "—"}
              </div>
              <div className="text-xs text-muted-foreground">
                {t("dashboard.percentOfSalary", { value: `${subsShareOfSalary}%` })}
              </div>
              <div className="text-[11px] text-muted-foreground">
                {t("dashboard.perYear", { value: formatNok(totals.yearly) })} · {totals.activeCount}{" "}
                {totals.activeCount === 1 ? t("dashboard.activeOne") : t("dashboard.activeMany")}
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-border p-5">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
              {t("dashboard.ringNoSalaryLabel")}
            </div>
            <div className="text-3xl font-display font-bold tabular-nums">
              {totals.monthly > 0 ? formatNok(totals.monthly) : "—"}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {t("dashboard.perYear", { value: formatNok(totals.yearly) })} · {totals.activeCount}{" "}
              {totals.activeCount === 1 ? t("dashboard.activeOne") : t("dashboard.activeMany")}
            </div>
            <p className="text-[11px] text-muted-foreground mt-3">
              {t("dashboard.ringNoSalaryHint")}
            </p>
          </div>
        )}

        {/* Secondary tiles */}
        <div className="grid grid-cols-3 gap-3 mt-5">
          {subsShareOfSalary != null && (
            <MiniStat label={t("dashboard.subsShareLabel")} value={`${subsShareOfSalary}%`} />
          )}
          <MiniStat
            label={t("dashboard.subsAnnualLabel")}
            value={totals.yearly > 0 ? formatNok(totals.yearly) : "—"}
          />
          <MiniStat
            label={t("dashboard.subsSavedLabel")}
            value={pausedSavingsMonthly > 0 ? formatNok(pausedSavingsMonthly) : "—"}
            sub={t("dashboard.subsSavedSub", { count: pausedSubs.length })}
            tone="positive"
          />
        </div>

        {isFree && hiddenSubs > 0 && (
          <div className="mt-3">
            <GateStatsNote />
          </div>
        )}
      </section>

      {/* Kjøpekraft */}
      <section className="animate-entry [animation-delay:100ms] border-t border-border pt-8">
        <div className="flex justify-between items-baseline mb-6">
          <h2 className="font-display font-bold text-sm tracking-tight inline-flex items-center gap-1">
            {t("dashboard.purchasingPower")}
            <InfoTooltip
              text={t("dashboard.purchasingPowerHelp")}
              label={t("dashboard.purchasingPower")}
            />
          </h2>
        </div>

        {growthRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("dashboard.emptySalary")}</p>
        ) : (
          <>
            {/* Paired hero tiles: nominal vs real raise for selected year */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <Metric
                label={t("dashboard.nominalRaise", { year: selected?.year ?? "" })}
                value={
                  selected?.growthPct != null
                    ? formatPercent(selected.growthPct, { signed: true })
                    : "—"
                }
                delta={null}
              />
              <div className="rounded-2xl border border-[color:var(--color-accent)]/40 bg-[color:var(--color-accent)]/5 p-3">
                <span className="block font-mono text-[9px] text-muted-foreground uppercase mb-1 inline-flex items-center gap-1">
                  {t("dashboard.realRaise")}
                  <InfoTooltip
                    text={t("dashboard.realGrowthHelp")}
                    label={t("dashboard.realRaise")}
                  />
                </span>
                <div className="text-lg font-display font-bold tabular-nums text-[color:var(--color-accent)]">
                  {selected?.realGrowthPct != null
                    ? formatPercent(selected.realGrowthPct, { signed: true })
                    : "—"}
                </div>
                {selected?.inflationPct != null && (
                  <div className="text-[10px] font-mono text-muted-foreground mt-1">
                    {t("dashboard.cpiInflation")}: {selected.inflationPct.toFixed(1)}%
                    {selected?.inflationKind === "tolvmanedersendring" && (
                      <span className="ml-1">· {t("dashboard.cpiProvisional")}</span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Salary tile kept */}
            <div className="grid grid-cols-1 gap-4 mb-6">
              <Metric
                label={`${t("dashboard.grossSalary")} · ${selected!.year}`}
                value={formatNok(selected!.salary)}
                delta={null}
              />
            </div>

            {isFree ? (
              <UpgradeLockPanel title={t("gate.cpiTitle")} body={t("gate.cpiBody")} />
            ) : (
              <>
                {nominalRealSeries.length >= 2 && baseYear && (
                  <div className="rounded-2xl border border-border p-4 mb-4">
                    <div className="flex items-baseline justify-between mb-3">
                      <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1">
                        {t("dashboard.nominalVsRealTitle")}
                        <InfoTooltip
                          text={t("dashboard.nominalVsRealHelp", { year: baseYear })}
                          label={t("dashboard.nominalVsRealTitle")}
                        />
                      </span>
                      <span className="font-mono text-[9px] text-muted-foreground flex gap-3">
                        <span className="inline-flex items-center gap-1">
                          <span className="inline-block w-3 h-[2px] bg-[color:var(--color-accent)]" />
                          {t("dashboard.legendNominal")}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <span className="inline-block w-3 border-t border-dashed border-foreground/60" />
                          {t("dashboard.legendReal", { year: baseYear })}
                        </span>
                      </span>
                    </div>
                    <NominalRealChart
                      series={nominalRealSeries}
                      selectedYear={selected?.year ?? null}
                      onSelect={(y) => setSelectedYear(y)}
                      ariaLabel={t("dashboard.nominalVsRealTitle")}
                    />
                  </div>
                )}

                {cumulative && (
                  <p className="text-sm text-muted-foreground leading-snug">
                    {t(
                      cumulative.realDiff >= 0
                        ? "dashboard.cumulativeAhead"
                        : "dashboard.cumulativeBehind",
                      {
                        year: cumulative.fromYear,
                        cpi: formatPercent(cumulative.cpiGrowth),
                        sal: formatPercent(cumulative.salaryGrowth),
                        diff: formatPercent(Math.abs(cumulative.realDiff)),
                      },
                    )}
                  </p>
                )}
              </>
            )}
          </>
        )}
      </section>

      {/* Subscriptions list */}
      <section className="animate-entry [animation-delay:200ms]">
        <div className="flex justify-between items-baseline mb-4">
          <h2 className="font-display font-bold text-sm tracking-tight">
            {t("dashboard.activeSubs")}
          </h2>
          {upcoming.length > 0 && (
            <Link
              to="/subscriptions"
              className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground"
            >
              {t("dashboard.seeAll")}
            </Link>
          )}
        </div>

        {upcoming.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("dashboard.emptyActive")}</p>
        ) : (
          <div className="divide-y divide-border border-t border-b border-border">
            {upcoming.map((s) => {
              const cycleLabel = t(`cycles.${s.billing_cycle}`);
              const monthly = monthlyEquivalent(Number(s.monthly_cost_nok_equiv), s.billing_cycle);
              const gated = subGateIds != null && !subGateIds.has(s.id);
              const row = (
                <div className="py-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="size-10 bg-card border border-border rounded-full grid place-items-center shrink-0">
                      <span className="text-[10px] font-mono font-medium">
                        {s.name.slice(0, 2).toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{s.name}</div>
                      <div className="text-[11px] font-mono text-muted-foreground truncate">
                        {t(`categories.${s.category}`)} · {t("subs.next")}: —
                      </div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-display font-bold tabular-nums">
                      {formatNok(monthly)}
                      <span className="text-[10px] font-mono font-normal text-muted-foreground">
                        {t("common.perMonth")}
                      </span>
                    </div>
                    <div className="text-[9px] font-mono text-muted-foreground uppercase tracking-wider">
                      {cycleLabel} · {formatMoney(Number(s.monthly_cost_nok), s.currency)}
                    </div>
                  </div>
                </div>
              );
              return gated ? (
                <BlurredGateWrapper key={s.id}>{row}</BlurredGateWrapper>
              ) : (
                <div key={s.id}>{row}</div>
              );
            })}
            {isFree && hiddenSubs > 0 && (
              <div className="py-2">
                <UpgradeGateRow hiddenCount={hiddenSubs} kind="subs" />
              </div>
            )}
          </div>
        )}
      </section>

      {/* Categories */}
      {totals.byCategory.length > 0 && (
        <section className="animate-entry [animation-delay:300ms]">
          <h2 className="font-display font-bold text-sm tracking-tight mb-4 inline-flex items-center gap-1">
            {t("dashboard.categories")}
            <InfoTooltip text={t("dashboard.categoriesHelp")} label={t("dashboard.categories")} />
          </h2>
          <div className="space-y-4">
            {totals.byCategory.map((c, i) => (
              <div key={c.category} className="space-y-1.5">
                <div className="flex justify-between text-[11px] font-medium">
                  <span>{t(`categories.${c.category}`)}</span>
                  <span className="font-mono tabular-nums">{formatNok(c.monthly)}</span>
                </div>
                <div className="h-1 bg-foreground/5 overflow-hidden">
                  <div
                    className="h-full bg-accent animate-grow"
                    style={
                      {
                        "--final-width": `${Math.max(2, c.share * 100)}%`,
                        animationDelay: `${i * 60}ms`,
                      } as CSSProperties & Record<`--${string}`, string>
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </AppShell>
  );
}

function Metric({
  label,
  value,
  delta,
  deltaLabel,
  badge,
  badgeTooltip,
}: {
  label: ReactNode;
  value: string;
  delta: number | null | undefined;
  deltaLabel?: string;
  badge?: string;
  badgeTooltip?: string;
}) {
  const positive = delta != null && delta >= 0;
  return (
    <div className="space-y-1">
      <span className="block font-mono text-[9px] text-muted-foreground uppercase">{label}</span>
      <div className="text-lg font-display font-bold tabular-nums flex items-baseline gap-1.5 flex-wrap">
        {value}
        {badge && (
          <span className="inline-flex items-center gap-1 text-[9px] font-mono font-normal normal-case text-muted-foreground border border-border rounded px-1 py-0.5">
            {badge}
            {badgeTooltip && <InfoTooltip text={badgeTooltip} label={badge} />}
          </span>
        )}
      </div>
      {delta != null && (
        <div
          className={
            "flex items-center gap-1 text-[10px] font-medium " +
            (positive ? "text-[color:var(--color-positive)]" : "text-[color:var(--color-negative)]")
          }
        >
          {positive ? <ArrowUpRight className="size-3" /> : <ArrowDownRight className="size-3" />}
          <span>
            {formatPercent(delta, { signed: true })} {deltaLabel ?? ""}
          </span>
        </div>
      )}
    </div>
  );
}

function MiniStat({
  label,
  value,
  sub,
  tone = "default",
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "default" | "positive" | "accent";
}) {
  const color =
    tone === "positive"
      ? "text-[color:var(--color-positive)]"
      : tone === "accent"
        ? "text-[color:var(--color-accent)]"
        : "text-foreground";
  return (
    <div className="rounded-2xl border border-border px-3 py-3 min-w-0">
      <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5 truncate">
        {label}
      </div>
      <div className={`text-base font-display font-bold tabular-nums truncate ${color}`}>
        {value}
      </div>
      {sub && (
        <div className="mt-1 text-[10px] font-mono text-muted-foreground truncate">{sub}</div>
      )}
    </div>
  );
}
